import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  afterNextRender,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CountryToken, EnvToken, LanguageToken } from '@app/core/env.tokens';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AnimationEvent,
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { buildConfig } from '@app/config/config';

import { Router } from '@angular/router';

import { JsonLdPipe } from '@app/pipes/json-ld-pipe';
import { TranslatePipe } from '@app/pipes/translate.pipe';

import { AuthService } from '@services/http/auth.service';
import { ExternalService } from '@app/services/http/external.service';
import { SEOService } from '@services/logic/seo.service';
import { StorageService } from '@services/logic/storage.service';
import { LocalizationService } from '@services/logic/localization.service';
import { TranslateService } from '@services/logic/translate.service';

import { IconComponent } from '@app/components/ui-icon/icon.component';

// Components
import { FooterComponent } from '@components/shared/footer/footer.component';
import { HowItWorksComponent } from '@components/seoPageElements/howItWorks/howItWorks.component';
import { LandingFaqComponent } from '@app/pages/landing-page/landingFaq/landingFaq.Component';
import { ReviewSliderComponent } from '@app/pages/landing-page/review-slider/review-slider.component';
import { ServiceOptionsComponent } from '@app/pages/landing-page/serviceOptions/serviceOptionsComponent';
import { WhyHandyhandComponent } from '@components/seoPageElements/whyPinployComponent/whyHandyhandComponent';
import { MatDialog } from '@angular/material/dialog';
import { VideoDialogComponent } from '@app/dialogs/videoDialog/videoDialog';
import { HowItWorksDialogComponent } from '@app/dialogs/howItWorksDialog/howItWorksDialog';
import { TrustpilotWidgetComponent } from '@app/components/seoPageElements/trustpilot-widget/trustpilot-widget.component';
import {
  DeepLinkButtonsComponent,
  DeepLinkButton,
} from '@components/shared/deep-link-buttons/deep-link-buttons.component';
import { MeetSomeHandyhandsComponent } from '@app/components/seoPageElements/meet-some-handyhands/meet-some-handyhands.component';
import { ServiceProviderValuePropComponent } from '@app/components/seoPageElements/service-provider-value-prop/service-provider-value-prop.component';
import { DamageCompensationLineComponent } from '@app/components/seoPageElements/damage-compensation-line/damage-compensation-line.component';

interface HeroContent {
  mainText: string;
  image: string;
}

type AnimationState = 'hidden' | 'visible';

@Component({
  selector: 'app-landing-page',
  imports: [
    FooterComponent,
    JsonLdPipe,
    TranslatePipe,
    IconComponent,
    HowItWorksComponent,
    WhyHandyhandComponent,
    LandingFaqComponent,
    ReviewSliderComponent,
    ServiceOptionsComponent,
    MeetSomeHandyhandsComponent,
    ServiceProviderValuePropComponent,
    CommonModule,
    TrustpilotWidgetComponent,
    DeepLinkButtonsComponent,
    DamageCompensationLineComponent,
  ],
  // Only services without providedIn: 'root' need component-level providers.
  providers: [AuthService, TranslatePipe],
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('animateText', [
      state('hidden', style({ opacity: 0, transform: 'translateY(10px)' })),
      state('visible', style({ opacity: 1, transform: 'translateY(0px)' })),
      transition('hidden => visible', [
        animate('0.6s cubic-bezier(0.4, 0, 0.2, 1)'),
      ]),
      transition('visible => hidden', [animate('0.3s ease-out')]),
    ]),
    trigger('animateImage', [
      state('hidden', style({ opacity: 0, transform: 'scale(0.95)' })),
      state('visible', style({ opacity: 1, transform: 'scale(1)' })),
      transition('hidden => visible', [
        animate('0.8s cubic-bezier(0.4, 0, 0.2, 1)'),
      ]),
      transition('visible => hidden', [animate('0.3s ease-out')]),
    ]),
  ],
})
export class LandingPageComponent implements OnInit, OnDestroy {
  private readonly env = inject(EnvToken);
  public readonly country = inject(CountryToken);
  protected readonly showTrustpilot =
    inject(LocalizationService).trustpilotSocialProofVisible;
  private readonly language = inject(LanguageToken);
  private readonly authService = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly router = inject(Router);
  private readonly seoService = inject(SEOService);
  private readonly storage = inject(StorageService);
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);

  protected readonly externalService = inject(ExternalService);

  private readonly config = buildConfig(this.env, this.country);
  public readonly isBrowser = isPlatformBrowser(this.platformId);

  // Section element queries used for smooth-scroll deep links.
  private readonly serviceOptionsSection = viewChild(ServiceOptionsComponent, {
    read: ElementRef,
  });
  private readonly trustpilotSection = viewChild(TrustpilotWidgetComponent, {
    read: ElementRef,
  });
  private readonly taskExamplesSection = viewChild(ReviewSliderComponent, {
    read: ElementRef,
  });
  private readonly meetSomeHandyhandsSection = viewChild(
    MeetSomeHandyhandsComponent,
    { read: ElementRef },
  );
  private readonly faqSection = viewChild(LandingFaqComponent, {
    read: ElementRef,
  });

  private readonly heroContent: readonly HeroContent[] = [
    {
      mainText: 'PAGES.LANDING.LP1.ANYTHING',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/everything_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.GARDEN',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/garden_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.MOVING',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/moving_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.CLEANING',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/cleaning_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.IT',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/it_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.PAINTER',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/painter_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.CARPENTER',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/carpenter_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.ELECTRICIAN',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/electrician_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.PLUMBING',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/plumbing_wetwork_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.BRICKLAYER',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/bricklayer_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.FLOOR',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/floow_work_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.FURNITURE_ASSEMBLY',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/furniture_assembly_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.FURNITURE',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/furniture_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.APPLIANCES',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/appliances_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.CARPORT',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/carport__1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.BIKE',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/bike_1x1.webp`,
    },
    {
      mainText: 'PAGES.LANDING.LP1.HANDYMAN',
      image: `${this.env.cloudFrontImgUrl}/private/kuno/handyman__1x1.webp`,
    },
  ];

  // View state — signals so change detection and SSR hydration stay in sync.
  protected readonly isNL = this.config.Basic.language === 'nl';
  protected readonly schema: Record<string, unknown>;

  // Trustpilot badge images (short = mobile, medium = tablet, long = desktop).
  // Danish artwork for da, English artwork for everything else (no NL asset).
  private readonly tpImgLocale = this.language === 'da' ? 'dk' : 'en';
  private readonly tpImgBase = `${this.env.cloudFrontImgUrl}/private/trustpilot/tp_horisontal`;
  protected readonly trustpilotImgShort = `${this.tpImgBase}_short_lenth_${this.tpImgLocale}_compressed.png`;
  protected readonly trustpilotImgMedium = `${this.tpImgBase}_medium_lenth_${this.tpImgLocale}_compressed.png`;

  protected readonly staticHeroText = signal(
    this.translatePipe.transform('PAGES.LANDING.LP1.TITLE_1'),
  );
  protected readonly dynamicHeroText = signal(
    this.translatePipe.transform('PAGES.LANDING.LP1.ANYTHING'),
  );
  protected readonly dynamicHeroImage = signal(this.heroContent[0].image);
  protected readonly innerWidth = signal(481);
  protected readonly isBelow770 = signal(true);
  protected readonly isRotationPaused = signal(false);
  protected readonly textAnimationState = signal<AnimationState>('hidden');
  protected readonly imageAnimationState = signal<AnimationState>('hidden');
  protected readonly deepLinkButtons = signal<DeepLinkButton[]>([]);

  // Hero rotation timers.
  private rotationFrameId: ReturnType<typeof requestAnimationFrame> | null =
    null;
  private contentUpdateTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private preloadTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastRotationTime = 0;
  private readonly rotationIntervalMs = 5000;
  private currentContentIndex = 0;

  // Dialog open guards.
  private isVideoDialogOpen = false;
  private isHowItWorksDialogOpen = false;

  private resizeListener: (() => void) | null = null;

  private readonly visibilityChangeHandler = (): void => {
    if (document.visibilityState === 'visible') {
      // Reset rotation timing when the tab becomes visible to avoid rapid jumps.
      this.lastRotationTime = Date.now();
    }
  };

  constructor() {
    this.schema = this.buildOrganizationSchema();

    afterNextRender(() => {
      this.preloadHeroImages();

      this.innerWidth.set(window.innerWidth);
      this.isBelow770.set(window.innerWidth <= 770);
      this.dynamicHeroImage.set(
        this.heroContent[this.currentContentIndex].image,
      );

      this.resizeListener = () => {
        const newWidth = window.innerWidth;
        this.innerWidth.set(newWidth);
        this.isBelow770.set(newWidth <= 770);
      };
      window.addEventListener('resize', this.resizeListener);

      this.startHeroContentRotation();
    });
  }

  ngOnInit(): void {
    this.redirectOnUserRole();
    this.setSeoMeta();
    this.setupDeepLinkButtons();
    this.setupVisibilityChangeListener();
  }

  ngOnDestroy(): void {
    if (this.rotationFrameId) {
      cancelAnimationFrame(this.rotationFrameId);
    }
    if (this.contentUpdateTimeoutId) {
      clearTimeout(this.contentUpdateTimeoutId);
    }
    if (this.preloadTimeoutId) {
      clearTimeout(this.preloadTimeoutId);
    }

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }

    if (typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler,
      );
    }

    this.textAnimationState.set('hidden');

    if (this.isVideoDialogOpen || this.isHowItWorksDialogOpen) {
      this.dialog.closeAll();
      this.isVideoDialogOpen = false;
      this.isHowItWorksDialogOpen = false;
    }
  }

  protected toggleRotationPause(): void {
    this.isRotationPaused.set(!this.isRotationPaused());
  }

  protected animationFinished(event: AnimationEvent): void {
    if (event.toState === 'hidden') {
      this.textAnimationState.set('visible');
      this.imageAnimationState.set('visible');
    }
  }

  protected imageAnimationFinished(event: AnimationEvent): void {
    if (event.toState === 'hidden') {
      this.imageAnimationState.set('visible');
    }
  }

  protected openVideoDialog(): void {
    if (this.isVideoDialogOpen) {
      return;
    }

    this.isVideoDialogOpen = true;
    const dialogRef = this.dialog.open(VideoDialogComponent, {
      disableClose: true,
      panelClass: 'full-screen-modal-mobile',
      width: '800px',
      maxWidth: 'unset',
      data: {
        title: this.translatePipe.transform('PAGES.HOW_IT_WORKS.HEADLINE'),
        vidChoice: 'howItWorks',
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.isVideoDialogOpen = false;
    });
  }

  protected openHowItWorksDialog(): void {
    if (this.isHowItWorksDialogOpen) {
      return;
    }

    this.isHowItWorksDialogOpen = true;
    const dialogRef = this.dialog.open(HowItWorksDialogComponent, {
      disableClose: true,
      panelClass: 'full-screen-modal-mobile',
      width: '500px',
      maxWidth: '90vw',
      data: {},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.isHowItWorksDialogOpen = false;
    });
  }

  protected scrollToServiceOptions(): void {
    this.scrollToSection(this.serviceOptionsSection);
  }

  protected scrollToTrustpilot(): void {
    this.scrollToSection(this.trustpilotSection);
  }

  protected scrollToTaskExamples(): void {
    this.scrollToSection(this.taskExamplesSection);
  }

  protected scrollToMeetSomeHandyhands(): void {
    this.scrollToSection(this.meetSomeHandyhandsSection);
  }

  protected scrollToFaq(): void {
    this.scrollToSection(this.faqSection);
  }

  private scrollToSection(section: Signal<ElementRef | undefined>): void {
    if (!this.isBrowser) {
      return;
    }

    section()?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  private setSeoMeta(): void {
    this.seoService.setSitelinksSearchboxSchema(this.router.url);
    this.seoService.setPageMeta({
      metaTitle: this.translatePipe.transform('PAGES.LANDING.META_TITLE'),
      metaDesc: this.translatePipe.transform('PAGES.LANDING.META_DESC'),
      canonicalUrl: `${this.env.domain}${this.router.url}`,
    });
  }

  private redirectOnUserRole(): Promise<boolean> | void {
    if (!this.isBrowser || !this.authService.isAuthenticated()) {
      return;
    }

    return this.storage?.user?.pinployeeOrTaskOwner === 'pp'
      ? this.externalService.goToTasks()
      : this.externalService.goToLanding();
  }

  private setupDeepLinkButtons(): void {
    this.deepLinkButtons.set([
      {
        label: 'COMPONENTS.HOW_IT_WORKS_DIALOG.TITLE',
        action: () => this.openHowItWorksDialog(),
        show: true,
        icon: 'play-circle',
        noHighlight: true,
      },
      {
        label: 'COMPONENTS.DEEP_LINK_BUTTONS.MEET_SOME_HANDYHANDS',
        action: () => this.scrollToMeetSomeHandyhands(),
        show: true,
        icon: 'user-multiple',
      },
      {
        label: 'COMPONENTS.DEEP_LINK_BUTTONS.EXAMPLES',
        action: () => this.scrollToTaskExamples(),
        show: true,
        icon: 'dashboard-square-01',
      },
      {
        label: 'COMPONENTS.DEEP_LINK_BUTTONS.PRICES',
        action: () => this.scrollToServiceOptions(),
        show: true,
        icon: 'tag-01',
      },
      {
        label: 'COMPONENTS.DEEP_LINK_BUTTONS.REVIEWS',
        action: () => this.scrollToTrustpilot(),
        show: this.showTrustpilot(),
        icon: 'star',
      },
    ]);
  }

  private setupVisibilityChangeListener(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener(
        'visibilitychange',
        this.visibilityChangeHandler,
      );
    }
  }

  private startHeroContentRotation(): void {
    if (!this.isBrowser) {
      return;
    }

    this.lastRotationTime = Date.now();

    // Preload the next image shortly before the first rotation.
    this.preloadTimeoutId = setTimeout(() => this.preloadNextImage(), 3000);

    // requestAnimationFrame keeps rotation smooth and tab-aware.
    this.scheduleNextRotation();
  }

  private scheduleNextRotation(): void {
    if (!this.isBrowser) {
      return;
    }

    const now = Date.now();
    if (
      now - this.lastRotationTime >= this.rotationIntervalMs &&
      !this.isRotationPaused()
    ) {
      this.rotateToNextContent();
      this.lastRotationTime = now;
    }

    this.rotationFrameId = requestAnimationFrame(() =>
      this.scheduleNextRotation(),
    );
  }

  private rotateToNextContent(): void {
    this.currentContentIndex =
      (this.currentContentIndex + 1) % this.heroContent.length;

    this.updateHeroContent();

    // Preload the upcoming image ~2s before it is shown.
    this.preloadTimeoutId = setTimeout(() => this.preloadNextImage(), 3000);
  }

  private updateHeroContent(): void {
    const content = this.heroContent[this.currentContentIndex];

    if (this.contentUpdateTimeoutId) {
      clearTimeout(this.contentUpdateTimeoutId);
    }

    // Fade out, then swap content mid-transition.
    this.textAnimationState.set('hidden');
    this.imageAnimationState.set('hidden');

    this.contentUpdateTimeoutId = setTimeout(() => {
      this.dynamicHeroText.set(this.translatePipe.transform(content.mainText));
      this.dynamicHeroImage.set(content.image);
    }, 300);
  }

  private preloadHeroImages(): void {
    // Preload the first hero image up front.
    this.appendImagePreload(this.heroContent[0].image);
  }

  private preloadNextImage(): void {
    const nextIndex = (this.currentContentIndex + 1) % this.heroContent.length;
    this.appendImagePreload(this.heroContent[nextIndex].image);
  }

  private appendImagePreload(href: string): void {
    if (document.querySelector(`link[href="${href}"]`)) {
      return;
    }

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    document.head.appendChild(link);
  }

  private buildOrganizationSchema(): Record<string, unknown> {
    const schemaI18n = this.translateService.data?.SEO?.SCHEMA;

    return {
      '@context': 'http://schema.org',
      '@type': 'Organization',
      url: this.env.domain,
      name: this.env.appName,
      logo: `${this.env.appUrl}/images/logo.png`,
      contactPoint: {
        '@type': 'ContactPoint',
        email: this.config.Basic.supportEmail,
        telephone: this.config.Basic.supportPhone,
        contactType: this.translatePipe.transform(
          'SEO.SCHEMA.LANDING_CONTACT_TYPE',
        ),
        url: `${this.env.domain}/hjaelp`,
        areaServed: this.config.Basic.language.toUpperCase(),
        availableLanguage: schemaI18n?.LANDING_AVAILABLE_LANGUAGES ?? [
          'Danish',
          'English',
        ],
      },
    };
  }
}
